import { Overlay } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NonClosableOverlayService } from '@y42/ag-grid';
import { connectState, InputObservable } from '@y42/angular/util';
import { ColumnTypeEnum, OperationModel, OperatorEnum } from '@y42/json-schema';
import { SelectOption } from '@y42/util/select-option';
import isEqual from 'lodash/isEqual';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  OperatorFunction,
  switchMap,
  tap,
} from 'rxjs';
import { OperationInputFormService } from './operation-input-form.service';
import {
  AbsoluteDateRangePreset,
  OperationInputColumn,
  OperationInputType,
  OperationInputTypeToColumnTypes,
  RelativeDateRangePreset,
} from './operation-input.model';

function showOperationInput(type: OperationInputType): OperatorFunction<ColumnTypeEnum, boolean> {
  return map((columnType) => OperationInputTypeToColumnTypes[type].includes(columnType));
}

interface VisibilityConfig {
  fieldName: boolean;
  applyButton: boolean;
  resetButton: boolean;
}

@UntilDestroy()
@Component({
  selector: 'y42-operation-input[column]',
  templateUrl: './operation-input.component.html',
  styleUrls: ['./operation-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    OperationInputFormService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OperationInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => OperationInputComponent),
      multi: true,
    },
    {
      provide: Overlay,
      useClass: NonClosableOverlayService,
    },
  ],
})
export class OperationInputComponent implements ControlValueAccessor {
  constructor(private readonly formService: OperationInputFormService) {
    this.patchFormOnOperationsAndColumnTypeChange().subscribe();
    this.autoEmitValuesWhenNoApplyButtonVisible().subscribe();
  }

  @HostBinding('class.filter-input-wrapper') clazz = true;

  private readonly operations$$ = new BehaviorSubject<OperationModel[]>([]);

  @Input()
  @InputObservable()
  column!: OperationInputColumn;
  private readonly column$!: Observable<OperationInputColumn | undefined>;

  @Input()
  operatorOptions?: Array<SelectOption<OperatorEnum>>;

  // only relevant for dates
  @Input() showRelativeDates = false;
  @Input() absoluteDateRangePresets?: AbsoluteDateRangePreset[];
  @Input() relativeDateRangePresets?: RelativeDateRangePreset[];
  @Input() minDate?: Date;
  @Input() maxDate?: Date;

  // only relevant for text
  @Input() showSelect = false;
  @Input() predefinedValues: string[] | null = null;

  // control
  @Input()
  @InputObservable()
  visibilityConfig: VisibilityConfig = {
    fieldName: true,
    applyButton: true,
    resetButton: true,
  };
  private readonly visibilityConfig$!: Observable<VisibilityConfig>;

  @Input() enableMoreConditions = true;

  @Output()
  appliedOperations: EventEmitter<void> = new EventEmitter();
  @Output()
  resetOperations: EventEmitter<void> = new EventEmitter();
  @Output()
  loadAutoProposal: EventEmitter<void> = new EventEmitter();

  readonly OperationInputType = OperationInputType;

  readonly form = this.formService.form;
  readonly operationsFormArray = this.form.controls.operations;

  private readonly columnType$ = this.column$.pipe(
    filter(Boolean),
    map((column) => column.data_type),
  );

  private readonly isFormValid$ = this.form.statusChanges.pipe(map((status) => status === 'VALID'));

  readonly state = connectState(this, {
    isFormValid: this.isFormValid$,

    showOperationInputBoolean: this.columnType$.pipe(showOperationInput(OperationInputType.Boolean)),
    showOperationInputDate: this.columnType$.pipe(showOperationInput(OperationInputType.Date)),
    showOperationInputNumber: this.columnType$.pipe(showOperationInput(OperationInputType.Number)),
    showOperationInputText: this.columnType$.pipe(showOperationInput(OperationInputType.Text)),
  });

  private onChange?: (operations: OperationModel[]) => void;
  private onTouched?: () => void;

  getOperations() {
    return this.formService.getOperations();
  }

  apply(viaClick = true) {
    this.form.markAsDirty();
    const operations = this.formService.getOperations();
    this.onChange?.(operations);
    if (viaClick) {
      this.appliedOperations.emit();
    }
  }

  reset() {
    this.formService.reset();
    this.operations$$.next([]);
    const operations = this.formService.getOperations();
    this.onChange?.(operations);
    this.resetOperations.emit();
  }

  writeValue(operations: OperationModel[]): void {
    this.operations$$.next(operations);
  }

  registerOnChange(fn: (operations: OperationModel[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    if (disabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  validate() {
    return this.form.invalid ? { invalid: true } : null;
  }

  private patchFormOnOperationsAndColumnTypeChange() {
    return combineLatest([
      this.operations$$.pipe(distinctUntilChanged((a, b) => a.length === b.length && isEqual(a, b))),
      this.columnType$.pipe(filter(Boolean), distinctUntilChanged()),
    ]).pipe(
      tap(([operations, columnType]) => this.formService.setOperations(operations, columnType)),
      untilDestroyed(this),
    );
  }

  private autoEmitValuesWhenNoApplyButtonVisible() {
    return this.visibilityConfig$.pipe(
      map((config) => config.applyButton),
      switchMap((applyButton) => {
        if (applyButton) {
          return of();
        }
        return this.formService.form.valueChanges.pipe(tap(() => this.apply(false)));
      }),
      untilDestroyed(this),
    );
  }
}
